import React, { useEffect, useRef, useState } from 'react'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { bareMetalService, sshService } from '../../../api'
import dedicatedPreorderService from '../../../api/dedicatedPreorderService'
import { Button, Counter, Input, MatchPassword, ModalVariables, Select, TopProgress } from '../../../components'
import { useUi } from '../../../context/ui.context'
import { formatHardwareFrequency } from '../../../utils/hardware-units'
import { PreorderDisk } from '../../../api/dedicatedPreorderTypes'
import { DedicatedServerPrice } from '../../../api/bareMetalTypes'
import { icons } from '../../../icons'
import { diskTypeLabel, formatCapacity } from '../list/format'
import { isDedicatedServiceInactiveError } from '../../../utils/dedicated-service'
import StockServers, { cpuSummary, diskSummary, StockServer } from './stock-servers'
import {
  Aside, Card, CardHead, CardTitle, EditLink, Eyebrow, Hint, InfoNote, Layout, Main, PickRow, Problem, ReviewCard,
  ReviewHead, ReviewRow, StepBadge, StepButton, Steps, Summary, SummaryActions,
  SummaryHead, SummaryRow, SummaryTitle, TextArea, TwoCols,
} from './ui'
import { apiErrorMessage, CreationDraft, CreationMode, CreationOptions, preorderPayload, serverPayload, validateConfiguration } from './model'
import OperatingSystemSelector from './operating-system'
import CreationSkeleton from './skeleton'
import DiskSelection from './disk-selection'
import Price, { PricePeriod, PricePeriodSelector } from './price'
import usePreorderPrice from './use-preorder-price'

type Props = {
  mode: CreationMode
  draft: CreationDraft
  saveDraft: (values: CreationDraft) => void
  onPreorder: () => void
  onBusy: (busy: boolean) => void
}

export default function CreationForm({ mode, draft, saveDraft, onPreorder, onBusy }: Props) {
  const [t] = useTranslation()
  const navigate = useNavigate()
  const { viewAlert } = useUi()
  const [step, setStep] = useState(0)
  const [pricePeriod, setPricePeriod] = useState<PricePeriod>('day')
  const [options, setOptions] = useState<CreationOptions>()
  const [optionsLoading, setOptionsLoading] = useState(true)
  const [optionsError, setOptionsError] = useState('')
  const [sshKeys, setSshKeys] = useState<{ value: string; label: string }[]>([])
  const [sshLoading, setSshLoading] = useState(false)
  const [sshError, setSshError] = useState('')
  const [submitError, setSubmitError] = useState('')
  const [passwordFocused, setPasswordFocused] = useState(false)
  const [sshModal, setSshModal] = useState<'' | 'generate-ssh' | 'create-ssh'>('')
  const mounted = useRef(true)
  const submitting = useRef(false)
  const optionsRequest = useRef(0)
  const stockPrices = useRef<Partial<Record<PricePeriod, Map<string, DedicatedServerPrice | null>>>>({})
  const sshRequest = useRef(0)
  const preorder = mode === 'preorder'
  const hasMissingPreorderComponents = Boolean(options && 'cpus' in options &&
    (!options.cpus.length || !options.rams.length || !options.disks.length || !options.os.length))

  const loadOptions = async (period: PricePeriod = pricePeriod, refresh = false) => {
    const request = ++optionsRequest.current
    if (refresh) stockPrices.current = {}
    setOptionsLoading(true)
    setOptionsError('')
    try {
      const response = preorder ? await dedicatedPreorderService.getCreateOptions() : await bareMetalService.getCreateOptions({ period })
      if (mounted.current && request === optionsRequest.current) {
        if ('servers' in response.data) {
          stockPrices.current[period] = new Map(response.data.servers.map(server => [server.id, server.price]))
        }
        setOptions(response.data)
      }
    } catch (error) {
      if (mounted.current && request === optionsRequest.current && !isDedicatedServiceInactiveError(error)) {
        setOptionsError(apiErrorMessage(error, t('ERROR_OCCURRED')))
      }
    } finally {
      if (mounted.current && request === optionsRequest.current) setOptionsLoading(false)
    }
  }
  const changePricePeriod = (period: PricePeriod) => {
    if (period === pricePeriod) return
    setPricePeriod(period)
    if (preorder) return
    const prices = stockPrices.current[period]
    if (options && 'servers' in options && prices && options.servers.every(server => prices.has(server.id))) {
      // A pending response for another period must not replace these prices.
      optionsRequest.current++
      setOptionsLoading(false)
      setOptionsError('')
      return
    }
    void loadOptions(period)
  }
  const loadSshKeys = async () => {
    const request = ++sshRequest.current
    setSshLoading(true)
    setSshError('')
    try {
      const response = await sshService.getSshKeys()
      if (mounted.current && request === sshRequest.current) {
        setSshKeys(response.data.objects.map((key: { id: string | number; name: string }) => ({ value: String(key.id), label: key.name })))
      }
    } catch (error) {
      if (mounted.current && request === sshRequest.current) setSshError(apiErrorMessage(error, t('ERROR_OCCURRED')))
    } finally {
      if (mounted.current && request === sshRequest.current) setSshLoading(false)
    }
  }
  useEffect(() => {
    mounted.current = true
    void loadOptions()
    void loadSshKeys()
    const invalidateRequests = () => { optionsRequest.current++; sshRequest.current++ }
    return () => { mounted.current = false; invalidateRequests(); onBusy(false) }
    // The parent remounts this form when mode or active client changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const form = useFormik<CreationDraft>({
    initialValues: draft,
    validate: values => {
      if (!options) return {}
      const errors = validateConfiguration(values, mode, options, t('REQUIRED_FIELD'), t('DEDICATED_MAX_LENGTH'), t('DEDICATED_POSITIVE_INTEGER'))
      if (preorder && values.cpu_count > 2) errors.cpu_count = t('DEDICATED_CPU_COUNT_RANGE')
      if (step > 0) {
        if (!values.password) errors.password = t('REQUIRED_FIELD')
        const allowsKeys = options.os.find(os => String(os.id) === values.os)?.allow_ssh_keys
        if (allowsKeys && values.ssh_key_ids.some(id => !sshKeys.some(key => key.value === id))) errors.ssh_key_ids = t('DEDICATED_KEY_UNAVAILABLE')
      }
      return errors
    },
    onSubmit: async values => {
      if (!options || optionsLoading || optionsError || hasMissingPreorderComponents || submitting.current) return
      if (step < 2) { setStep(step + 1); return }
      submitting.current = true
      onBusy(true)
      setSubmitError('')
      try {
        if (preorder) await dedicatedPreorderService.create(preorderPayload(values, options))
        else await bareMetalService.createServer(serverPayload(values, options))
        if (!mounted.current) return
        viewAlert({ severity: 'success', message: String(t(preorder ? 'DEDICATED_PREORDER_CREATED' : 'DEDICATED_CREATION_STARTED')) })
        navigate(preorder ? '/bare-metal?view=preorders' : '/bare-metal')
      } catch (error) {
        if (mounted.current && !isDedicatedServiceInactiveError(error)) setSubmitError(apiErrorMessage(error, t('ERROR_OCCURRED')))
      } finally {
        submitting.current = false
        if (mounted.current) onBusy(false)
      }
    },
  })
  const { values } = form
  useEffect(() => { saveDraft(values) }, [values, saveDraft])
  const selectedOs = options?.os.find(os => String(os.id) === values.os)
  const servers: StockServer[] = options && 'servers' in options ? options.servers.map(server => ({
    id: server.id, name: server.name || server.id, hardware: server.hardware,
    price: stockPrices.current[pricePeriod]?.get(server.id) ?? null,
  })) : []
  const selectedServer = servers.find(server => server.id === values.server_id)
  const preorderOptions = options && 'cpus' in options ? options : undefined
  const cpu = preorderOptions?.cpus.find(item => item.id === values.cpu_id)
  const ram = preorderOptions?.rams.find(item => item.id === values.ram_id)
  const chosenDisks = values.disk_ids.map(id => preorderOptions?.disks.find(item => item.id === id)).filter(Boolean) as PreorderDisk[]
  const canPricePreorder = Boolean(cpu && ram && values.disk_ids.length && chosenDisks.length === values.disk_ids.length &&
    Number.isSafeInteger(values.cpu_count) && values.cpu_count >= 1 && values.cpu_count <= 2 &&
    Number.isSafeInteger(values.ram_count) && values.ram_count >= 1)
  const preorderPrice = usePreorderPrice(canPricePreorder ? {
    cpu_id: values.cpu_id, cpu_count: values.cpu_count,
    ram_id: values.ram_id, ram_count: values.ram_count,
    disk_ids: values.disk_ids, period: pricePeriod,
  } : undefined)
  const allowsKeys = Boolean(selectedOs?.allow_ssh_keys)
  const keyNames = values.ssh_key_ids.map(id => sshKeys.find(key => key.value === id)?.label).filter(Boolean)

  const dash = '—'
  const groupLines = (items: string[]) => {
    const counts = new Map<string, number>()
    items.forEach(item => counts.set(item, (counts.get(item) || 0) + 1))
    return Array.from(counts, ([label, count]) => `${count} × ${label}`)
  }
  const hardwareRows = preorder ? [
    { key: t('DEDICATED_PROCESSOR'), lines: cpu ? [`${values.cpu_count} × ${cpu.name || cpu.model}`, `${cpu.cores * values.cpu_count} ${t('DEDICATED_CORES_TOTAL')}`] : [dash] },
    { key: t('DEDICATED_MEMORY'), lines: ram ? [formatCapacity(ram.size_mb * values.ram_count, 'MB', true), `${values.ram_count} × ${formatCapacity(ram.size_mb, 'MB', true)} ${t('DEDICATED_MODULES_SUFFIX')}`] : [dash] },
    { key: t('STORAGE'), lines: chosenDisks.length ? [
      ...groupLines(chosenDisks.map(disk => `${formatCapacity(disk.size_gb, 'GB')} ${diskTypeLabel(disk.disk_type)}`.trim())),
      ...(chosenDisks.length > 1 ? [`${t('DEDICATED_TOTAL')} ${formatCapacity(chosenDisks.reduce((sum, disk) => sum + disk.size_gb, 0), 'GB')}`] : []),
    ] : [dash] },
  ] : [
    { key: t('DEDICATED_SERVER'), lines: [selectedServer?.name || dash] },
    { key: t('DEDICATED_PROCESSOR'), lines: selectedServer ? (() => { const item = cpuSummary(selectedServer.hardware.cpu, String(t('DEDICATED_CORES'))); return [item.title, item.sub].filter(Boolean) })() : [dash] },
    { key: t('DEDICATED_MEMORY'), lines: [selectedServer ? formatCapacity(selectedServer.hardware.ram.value, selectedServer.hardware.ram.value_type, true) : dash] },
    { key: t('STORAGE'), lines: selectedServer ? (() => { const item = diskSummary(selectedServer.hardware.disk); return [...item.groups.map(group => group.text), ...(item.total ? [`${t('DEDICATED_TOTAL')} ${item.total}`] : [])] })() : [dash] },
  ]
  const accessLines = [
    t(values.password ? 'DEDICATED_PASSWORD_SET' : 'DEDICATED_PASSWORD_UNSET'),
    allowsKeys && keyNames.length ? t('DEDICATED_SSH_KEYS_COUNT', { count: keyNames.length }) : t('DEDICATED_NO_SSH_KEYS'),
  ]
  const summary = [
    { key: t('HOSTNAME'), lines: [values.hostname || dash] },
    ...hardwareRows,
    { key: t('OS'), lines: [selectedOs?.name || dash] },
    ...(step > 0 ? [{ key: t('DEDICATED_ACCESS'), lines: accessLines }] : []),
  ]
  const review = [
    { title: t(preorder ? 'DEDICATED_PREORDER' : 'DEDICATED_SERVER'), step: 0, rows: [
      ...(preorder && values.name ? [{ key: t('DEDICATED_PREORDER_NAME'), value: values.name }] : []),
      { key: t('HOSTNAME'), value: values.hostname || dash },
      ...hardwareRows.map(row => ({ key: row.key, value: row.lines.join('\n') })),
      ...(preorder && values.comment ? [{ key: t('DEDICATED_REQUIREMENTS'), value: values.comment }] : []),
    ] },
    { title: t('OS'), step: 0, rows: [{ key: t('DEDICATED_IMAGE'), value: selectedOs?.name || dash }] },
    { title: t('DEDICATED_ACCESS'), step: 1, rows: [
      { key: t('ROOT_PASSWORD_LABEL'), value: values.password ? '••••••••' : dash },
      { key: t('SSH_KEY'), value: allowsKeys ? keyNames.join(', ') || t('DEDICATED_NONE') : t('DEDICATED_NOT_SUPPORTED') },
    ] },
  ]
  const unavailable = options && !preorder && (!servers.length || !options.os.length)
  const fieldProps = { values, errors: form.errors, touched: form.touched, handleChange: form.handleChange, handleBlur: form.handleBlur }
  const selectErrors = form.submitCount > 0 ? form.errors : undefined
  const steps = [t('DEDICATED_STEP_CONFIGURE'), t('DEDICATED_STEP_ACCESS'), t('DEDICATED_STEP_REVIEW')]
  const goBack = () => { setSubmitError(''); setStep(step - 1) }
  const canSubmit = !optionsLoading && !optionsError && !hasMissingPreorderComponents && !unavailable && Boolean(options)

  return <>
    <TopProgress loading={(!options && optionsLoading) || form.isSubmitting} />
    <Steps aria-label={String(t('DEDICATED_STEPS'))}>
      {steps.map((label, index) => {
        const state = index < step ? 'done' : index === step ? 'current' : 'next'
        return <li key={label} style={{ display: 'flex', flex: '1 1 140px' }}>
          <StepButton type="button" $state={state} aria-current={state === 'current' ? 'step' : undefined}
            disabled={state !== 'done' || form.isSubmitting} onClick={() => { setSubmitError(''); setStep(index) }}>
            <StepBadge $state={state} aria-hidden="true">{state === 'done' ? '✓' : index + 1}</StepBadge>
            {label}
          </StepButton>
        </li>
      })}
    </Steps>
    <PricePeriodSelector value={pricePeriod} onChange={changePricePeriod} />
    {optionsError ? <Problem role="alert">
      <span>{optionsError}</span>
      <Button title={t('REFRESH_LIST')} variant="stroke" size="small" onClick={() => loadOptions(pricePeriod, true)} loading={optionsLoading} />
    </Problem> : null}
    {optionsLoading && !options && <CreationSkeleton mode={mode} step={step} />}
    {!optionsLoading && unavailable ? <Card>
      <CardHead>
        <CardTitle>{t('NO_FREE_SERVERS')}</CardTitle>
        <Hint>{t('DEDICATED_NO_STOCK_HINT')}</Hint>
      </CardHead>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <Button title={t('DEDICATED_PREORDER')} variant="primary" size="display" onClick={onPreorder} />
        <Button title={t('REFRESH_LIST')} variant="stroke" size="display" onClick={() => loadOptions(pricePeriod, true)} loading={optionsLoading} />
      </div>
    </Card> : null}
    {options && !unavailable ? <Layout>
      <Main>
        {step === 0 ? <>
          <Card aria-labelledby="dedicated-identity-title">
            <CardTitle id="dedicated-identity-title">{t('DEDICATED_IDENTITY')}</CardTitle>
            <TwoCols>
              {preorder && <Input title={t('DEDICATED_PREORDER_NAME')} placeholder={t('DEDICATED_PREORDER_NAME_PLACEHOLDER')} fieldName="name" {...fieldProps} />}
              <Input required title={t('HOSTNAME')} placeholder="server.example.com" fieldName="hostname" {...fieldProps} />
            </TwoCols>
          </Card>
          {!preorder ? <StockServers servers={servers} value={values.server_id} onPreorder={onPreorder}
            pricePeriod={pricePeriod} pricesLoading={optionsLoading}
            onChange={id => { void form.setFieldTouched('server_id', true, false); void form.setFieldValue('server_id', id) }}
            error={selectErrors?.server_id ? t('DEDICATED_PICK_SERVER') : undefined} />
          : preorderOptions ? <Card aria-labelledby="dedicated-hardware-title">
            <CardHead>
              <CardTitle id="dedicated-hardware-title">{t('DEDICATED_HARDWARE')}</CardTitle>
              <Hint>{t('DEDICATED_HARDWARE_DESCRIPTION')}</Hint>
            </CardHead>
            <PickRow>
              <Select required title={t('DEDICATED_PROCESSOR')} fieldName="cpu_id" data={preorderOptions.cpus.map(item => ({ value: item.id, label: `${item.name || item.model} · ${item.cores} ${t('DEDICATED_CORES')} / ${item.threads} ${t('DEDICATED_THREADS')} · ${formatHardwareFrequency(item.speed_mhz, 'MHz')}` }))}
                selectedValue={values.cpu_id} onChange={value => form.setFieldValue('cpu_id', value)} errors={preorderOptions.cpus.length ? selectErrors : undefined} touched={form.touched} hasSearch
                disabled={!preorderOptions.cpus.length} placeholder={!preorderOptions.cpus.length ? t('DEDICATED_NO_COMPONENT_OPTIONS') : undefined} />
              {preorderOptions.cpus.length > 0 && <Counter title={t('DEDICATED_SOCKETS')} fieldName="cpu_count" value={values.cpu_count} minCount={1} maxCount={2} hideItems="CPU"
                onChange={(value: number | string) => form.setFieldValue('cpu_count', Number(value))} errors={form.errors} touched={form.touched} />}
            </PickRow>
            <PickRow>
              <Select required title={t('DEDICATED_MEMORY_MODULE')} fieldName="ram_id" data={preorderOptions.rams.map(item => ({ value: item.id, label: `${item.name || item.model} · ${formatCapacity(item.size_mb, 'MB', true)}` }))}
                selectedValue={values.ram_id} onChange={value => form.setFieldValue('ram_id', value)} errors={preorderOptions.rams.length ? selectErrors : undefined} touched={form.touched} hasSearch
                disabled={!preorderOptions.rams.length} placeholder={!preorderOptions.rams.length ? t('DEDICATED_NO_COMPONENT_OPTIONS') : undefined} />
              {preorderOptions.rams.length > 0 && <Counter title={t('DEDICATED_MODULES')} fieldName="ram_count" value={values.ram_count} minCount={1} hideItems="pcs"
                onChange={(value: number | string) => form.setFieldValue('ram_count', Number(value))} errors={form.errors} touched={form.touched} />}
            </PickRow>
            <DiskSelection disks={preorderOptions.disks} selectedIds={values.disk_ids}
              onChange={ids => form.setFieldValue('disk_ids', ids)}
              error={preorderOptions.disks.length > 0 && typeof selectErrors?.disk_ids === 'string' ? selectErrors.disk_ids : undefined} />
          </Card> : null}
          <OperatingSystemSelector systems={options.os} value={values.os} onChange={value => {
            void form.setFieldValue('os', value)
            if (!options.os.find(os => String(os.id) === value)?.allow_ssh_keys) void form.setFieldValue('ssh_key_ids', [])
          }} error={options.os.length ? selectErrors?.os : undefined} />
          {preorder && <Card aria-labelledby="dedicated-comment-title" style={{ gap: 8 }}>
            <label id="dedicated-comment-title" htmlFor="dedicated-comment" style={{ fontSize: 15, lineHeight: '20px', fontWeight: 500 }}>
              {t('DEDICATED_COMMENT')} <Aside>· {t('DEDICATED_OPTIONAL')}</Aside>
            </label>
            <TextArea id="dedicated-comment" name="comment" value={values.comment} placeholder={t('DEDICATED_COMMENT_HINT')}
              onChange={form.handleChange} onBlur={form.handleBlur} maxLength={2000} />
          </Card>}
        </> : step === 1 ? <>
          <Card aria-labelledby="dedicated-password-title">
            <CardTitle id="dedicated-password-title">{t('DEDICATED_ROOT_PASSWORD')}</CardTitle>
            <div style={{ position: 'relative' }}>
              <Input required title={t('ROOT_PASSWORD_LABEL')} placeholder="" fieldName="password" isPassword autoComplete="new-password"
                onFocus={() => setPasswordFocused(true)} onFocusOut={() => setPasswordFocused(false)} {...fieldProps} />
              <MatchPassword validateString={values.password} active={passwordFocused} setEnabled={() => undefined} />
            </div>
          </Card>
          <Card aria-labelledby="dedicated-ssh-title">
            <CardHead>
              <CardTitle id="dedicated-ssh-title">{t('SSH_KEY')} <Aside>· {t('DEDICATED_OPTIONAL')}</Aside></CardTitle>
              <Hint>{t('DEDICATED_SSH_DESCRIPTION')}</Hint>
            </CardHead>
            <Select multi title={t('SSH_KEY')} hideTitle fieldName="ssh_key_ids" data={sshKeys}
              selectedValue={values.ssh_key_ids.map(id => sshKeys.find(key => key.value === id) || { value: id, label: id })}
              onChange={() => undefined} multipleOnChange={key => form.setFieldValue('ssh_key_ids', values.ssh_key_ids.includes(key.value)
                ? values.ssh_key_ids.filter(id => id !== key.value) : [...values.ssh_key_ids, key.value])}
              errors={form.errors} touched={form.touched} disabled={!allowsKeys || sshLoading} />
            {!allowsKeys && <InfoNote>{t('DEDICATED_SSH_UNSUPPORTED')}</InfoNote>}
            {sshError && <Problem role="alert">{sshError}</Problem>}
            {allowsKeys && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              <Button variant="stroke" size="mid" title={t('CREATE_NEW_KEY')} onClick={() => setSshModal('generate-ssh')} />
              <Button variant="stroke" size="mid" title={t('ADD_PUBLIC_SSH')} onClick={() => setSshModal('create-ssh')} />
              <Button variant="stroke" size="mid" title={t('REFRESH_LIST')} onClick={loadSshKeys} loading={sshLoading} />
            </div>}
          </Card>
        </> : <>
          {review.map(group => <ReviewCard key={group.title} aria-label={group.title}>
            <ReviewHead>
              <CardTitle>{group.title}</CardTitle>
              <EditLink type="button" onClick={() => { setSubmitError(''); setStep(group.step) }} disabled={form.isSubmitting}>
                <img src={icons.Edit} alt="" />{t('EDIT')}
              </EditLink>
            </ReviewHead>
            {group.rows.map(row => <ReviewRow key={row.key}><span>{row.key}</span><span>{row.value}</span></ReviewRow>)}
          </ReviewCard>)}
          {preorder && <InfoNote>{t('DEDICATED_PREORDER_REVIEW_HINT')}</InfoNote>}
        </>}
        {submitError && <Problem role="alert">{submitError}</Problem>}
      </Main>
      <Summary aria-label={String(t('DEDICATED_CONFIGURATION'))}>
        <SummaryHead>
          <Eyebrow>{t(preorder ? 'DEDICATED_PREORDER' : 'DEDICATED_IN_STOCK')}</Eyebrow>
          <SummaryTitle>{(preorder && values.name) || values.hostname || t('DEDICATED_NEW_SERVER')}</SummaryTitle>
        </SummaryHead>
        {summary.map(row => <SummaryRow key={row.key} $empty={row.lines[0] === dash}>
          <Eyebrow>{row.key}</Eyebrow>
          {row.lines.map(line => <span key={line}>{line}</span>)}
        </SummaryRow>)}
        <SummaryRow aria-live="polite" aria-busy={preorder ? preorderPrice.loading : optionsLoading}>
          <Eyebrow>{t('ESTIMATED_COST')}</Eyebrow>
          {preorder ? !canPricePreorder ? <Hint>{t('SELECT_OPTIONS_TO_DISPLAY_PRICE')}</Hint>
            : preorderPrice.waiting ? <Hint>{dash}</Hint>
            : preorderPrice.loading ? <Hint>{t('LOADING')}</Hint>
            : <>
              <Price price={preorderPrice.price} period={pricePeriod} />
              {preorderPrice.failed && <Button variant="stroke" size="small" title={t('DEDICATED_RETRY_PRICE')} onClick={preorderPrice.retry} />}
            </>
            : selectedServer ? <Price price={selectedServer.price} period={pricePeriod} loading={optionsLoading} /> : <Hint>{t('SELECT_OPTIONS_TO_DISPLAY_PRICE')}</Hint>}
        </SummaryRow>
        <SummaryActions>
          <Button variant="primary" size="display" title={t(step < 2 ? 'CONTINUE' : preorder ? 'DEDICATED_SUBMIT_PREORDER' : 'CREATE')}
            onClick={() => form.handleSubmit()} loading={form.isSubmitting} disabled={!canSubmit} focusOnValidationError
            customStyles={{ width: '100%' }} />
          <div>
            {step > 0 && <Button variant="stroke" size="display" title={t('BACK')} disabled={form.isSubmitting} onClick={goBack} />}
            <Button variant="stroke" size="display" title={t('CANCEL')} disabled={form.isSubmitting} onClick={() => navigate('/bare-metal')} />
          </div>
        </SummaryActions>
      </Summary>
    </Layout> : null}
    {sshModal && <ModalVariables modalType={sshModal} viewModal activeAnimation closeModal={() => { setSshModal(''); void loadSshKeys() }} closeModalCancel={() => setSshModal('')} />}
  </>
}
